import dynamic from 'next/dynamic';
import { Anchor, Banner, Box, List, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import {
  isDisplayableTimestamp,
  MESSAGE_LOAD_ERROR_TEXT,
  MESSAGE_STATUS,
  NewMessageNotificationBanner,
} from '@zg-rentals/satellite-components';
import { format, isSameDay, isThisYear, isToday, isYesterday } from 'date-fns';
import React, { forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import { useIsVisible } from '@zg-rentals/react-hooks';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  SatelliteConversationFeatureType,
  SatelliteUiTreatmentType,
  type SatelliteWebLandlordConversationGetMessagesRequestDto,
  SatelliteWebLandlordConversationHandler_getMessages,
} from '@zg-rentals/ts-satellite-client';
import { useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata } from '@zg-rentals/ts-satellite-client-react-query';
import { ModalType } from '../../types/ModalType';
import { preventMessageListScrollStore } from '../../lib/inboxStores';
import useModal from '../../hooks/useModal';
import { AppContext } from '../../contexts/AppContextProvider';
import { isHttpAuthError } from '../../utils/errorUtils';
import stringUtils from '../../utils/stringUtils';
import isValidListingAlias from '../../utils/isValidListingAlias';
import isValidConversationId from '../../utils/isValidConversationId';
import { getComposerDisabledText } from '../../utils/composerRestrictions';
import type { AttachmentsForMessage } from '../MessageAttachments';
import MessageItem, { type OnSystemMessageCtaClick } from '../MessageItem';
import DeleteAttachments from '../Modals/DeleteAttachments';
import RenterProfileChatBubble from '../RenterProfileChatBubble';
import EmptyContainer from '../MessageList/components/EmptyContainer';
import { MessageListContentsSkeleton } from './skeleton';
import ErrorContainer from './components/ErrorContainer';
import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import { TRIGGER_LOCATION, type TriggerLocation } from '../../constants/clickstreamEvents/globals';
import StringConstants from '../../constants/StringConstants';
import { useUserWarnings } from '../../hooks/useUserWarnings';
import { appMarkerLogger, bootstrap } from '../../bootstrap';
import { scrollbarStyles } from '../../styles/scrollbar';
import { useConversationMessagesQuery } from '../../hooks/useConversationMessagesQuery';
import useLeadRestrictions from '../../hooks/useLeadRestrictions';
import useMessageListRealtimeUpdates from '../../hooks/useMessageListRealtimeUpdates';
import {
  type ConversationMessage,
  FAILED_TO_LOAD_MESSAGE_TYPE,
  mapGetMessagesResponseToConversationData,
  markMessageAttachmentsDeleted,
} from '../../utils/conversationMessageUtils';
import { logError } from '@zg-rentals/log-error';

const logger = appMarkerLogger.child({ name: 'MessageListItems' });

export type ConversationIdentity = {
  clientTimeZone: string;
  conversationId: string;
  listingAlias: string | undefined;
};

export type OnResendMessageComplete = (args: { conversation: ConversationIdentity }) => void;

type OpenDeleteAttachmentsModalArgs = {
  attachments: AttachmentsForMessage;
  messageId: string;
};

type OpenImagesViewerArgs = {
  imageAttachments: AttachmentsForMessage;
  selectedImageIndex: number;
};

export type HandleOpenDeleteAttachmentsModal = ({ attachments, messageId }: OpenDeleteAttachmentsModalArgs) => void;
export type HandleOpenImagesViewer = ({ imageAttachments, selectedImageIndex }: OpenImagesViewerArgs) => void;

const ImagesViewer = dynamic(() => import('../ImagesViewer'), {
  loading: () => null,
  ssr: false,
});

const POLL_INTERVAL_IN_MS = 60 * 1000;
const DATE_FNS_FORMAT = 'EEEE, MMM do';
const DATE_FNS_FORMAT_WITH_YEAR = 'MMM do, yyyy';

// Polyfill smooth scroll for safari :(
if (typeof window !== 'undefined' && window !== null) {
  smoothscroll.polyfill();
}

export type MessageListItemsProps = {
  conversation: ConversationIdentity;
  leadId?: string | null;
  showEmptyContainer?: boolean;
  shouldRefetchAttachments?: boolean;
  showMoveToInboxBanner?: boolean;
  onResendMessageComplete?: OnResendMessageComplete;
  // `getMessages` doesn't carry this; the parent passes the value it already holds for the composer.
  isPhoneLead?: boolean;
  onConversationChange?: () => void;
  onRenterProfileClick: () => void;
  onSystemMessageCtaClick: OnSystemMessageCtaClick;
  onScrollComplete: () => void;
  triggerLocation: TriggerLocation;
  // Whether the message list is the currently visible view. On mobile the inbox columns stay
  // mounted but `display: none` when not active, which makes the end-of-list IntersectionObserver
  // report "not at the bottom" even though the user isn't looking. Defaults to true so non-inbox
  // callers (e.g. the renter messaging modal) are unaffected.
  isMessageListVisible?: boolean;
};

const MessageListItems = forwardRef<HTMLDivElement, MessageListItemsProps>(function MessageListItems(
  {
    conversation,
    leadId,
    showEmptyContainer = false,
    shouldRefetchAttachments = false,
    showMoveToInboxBanner = false,
    onResendMessageComplete,
    isPhoneLead = false,
    onConversationChange,
    onRenterProfileClick,
    onSystemMessageCtaClick,
    onScrollComplete,
    triggerLocation,
    isMessageListVisible = true,
  },
  ref,
) {
  const { conversationId, listingAlias, clientTimeZone } = conversation;
  const queryClient = useQueryClient();
  const { conversationThreadWarningData } = useUserWarnings();
  const preventMessageListScroll = preventMessageListScrollStore.useValue();

  // Refs
  // `messageListEndRef` doubles as the IntersectionObserver target (via `useIsVisible`)
  // and the scroll target for `scrollIntoView`. `isMessageListEndVisible` flips true
  // whenever the sentinel enters the viewport — including from a manual user scroll,
  // which is what the programmatic `scrollToBottom` path can't observe on its own.
  const { ref: messageListEndRef, isVisible: isMessageListEndVisible } = useIsVisible<HTMLDivElement>();
  const currentConversationId = useRef<string | undefined>('');
  const onScrollCompleteRef = useRef(onScrollComplete);
  onScrollCompleteRef.current = onScrollComplete;
  // Track pending scroll-to-bottom timers so we can cancel them on unmount; otherwise a
  // late-firing timer runs setState against a torn-down tree (e.g. jsdom in tests where
  // `window` is gone), which surfaces as "ReferenceError: window is not defined".
  const scrollTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // State
  const [prevMessageCount, setPrevMessageCount] = useState<number | null>(null);
  const [showPaginationLoading, setShowPaginationLoading] = useState(false);
  const [showNewMessageNotification, setShowNewMessageNotification] = useState(false);
  const [prevFullMessageCount, setPrevFullMessageCount] = useState<number>(0);
  /**
   * Older pages loaded via "Load more messages" are stored here (in component state) rather
   * than in the React Query cache. The polling `useConversationMessagesQuery` would otherwise
   * overwrite the cache with just the latest page on each poll, dropping any older pages.
   * Reset when the conversation changes.
   */
  const [olderMessages, setOlderMessages] = useState<Array<ConversationMessage>>([]);
  const [imagesViewerData, setImagesViewerData] = useState<{
    imageAttachments: AttachmentsForMessage;
    selectedImageIndex: number;
  } | null>(null);
  const [deleteAttachmentsModalData, setDeleteAttachmentsModalData] = useState<{
    attachments: AttachmentsForMessage;
    messageId: string;
  } | null>(null);
  const [hasAuthError, setHasAuthError] = useState(false);
  const { isImpersonator, setHasMessageListError } = useContext(AppContext);
  const { activeModal, setActiveModal } = useModal();
  const enableWebsocketIntegration = getBooleanConfig('enableWebsocketIntegration', true);

  const scrollToBottom = useCallback(() => {
    if (messageListEndRef.current) {
      messageListEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'end',
      });
    }
    setShowNewMessageNotification(false);
    onScrollCompleteRef.current();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  const scheduleScrollToBottom = useCallback(
    (delay: number) => {
      const id = setTimeout(() => {
        scrollTimeoutsRef.current.delete(id);
        scrollToBottom();
      }, delay);
      scrollTimeoutsRef.current.add(id);
    },
    [scrollToBottom],
  );

  useEffect(
    () => () => {
      scrollTimeoutsRef.current.forEach(clearTimeout);
      scrollTimeoutsRef.current.clear();
    },
    [],
  );

  // Polling stops on 4xx errors (non-recoverable) — see RCRM-6376.
  const {
    data: messageListData,
    isLoading,
    error,
    refetch,
  } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    pollInterval: enableWebsocketIntegration ? undefined : POLL_INTERVAL_IN_MS,
  });

  // A conversation that can't send must not offer a resend that routes around the composer's
  // gate — the restriction and the composer's local reasons each close it, with their own copy.
  const { canSendMessage: isResendAllowedByRestrictions, getDisabledMessage } = useLeadRestrictions(
    messageListData ?? null,
  );
  const resendBlockedText = getComposerDisabledText({
    canSendMessage: isResendAllowedByRestrictions,
    sendDisabledMessage: getDisabledMessage(SatelliteConversationFeatureType.SEND_MESSAGE),
    isImpersonator,
    isPhoneLead,
    isSpam: messageListData?.status?.isSpam ?? false,
    isConversationBlockedByZillow: messageListData?.isConversationBlockedByZillow ?? false,
  });

  // Equivalent of the previous Apollo `onCompleted` callback — run UI-side
  // effects whenever new data arrives.
  const hasReceivedDataRef = useRef(false);
  // Snapshot of the previous latest page so we can detect messages that rolled out of the
  // latest-N window (e.g. a new message arrived and pushed the oldest of the 10 displayed
  // out). Without this, those messages would simply disappear from the UI on the next poll.
  const prevLatestPageRef = useRef<Array<ConversationMessage>>([]);
  // Tracks "we just switched into this conversation and the next data update should be
  // treated as the initial-entry render, even if RQ already served stale cache." Without
  // this, the conv-switch sequence (stale cache served immediately → background refetch
  // resolves) splits the conv-change effect across two useEffect runs: run 1 sees stale
  // `hasUnreadMessage: false` and the scroll-to-bottom it schedules triggers
  // `handleScrollComplete` against stale data (so mark-read bails); run 2 sees fresh
  // `hasUnreadMessage: true` but its `isMessageListEndVisible` check is false
  // because the smooth scroll from run 1 hasn't completed yet, so it shows the
  // new-message notification banner instead of scrolling and never marks the
  // conversation as read.
  const isAwaitingFreshDataAfterConvSwitchRef = useRef(false);
  useEffect(() => {
    if (!messageListData) return;
    const isFirstData = !hasReceivedDataRef.current;
    hasReceivedDataRef.current = true;

    if (isFirstData && prevMessageCount === null) {
      setPrevMessageCount(messageListData.pagination.pageConversationCount);
      setPrevFullMessageCount(messageListData.pagination.queryFullMessageCount);
    }

    // Detect messages that were in the previous latest page but aren't in the new one — they
    // rolled out of the window. Preserve them in `olderMessages` so they remain visible.
    //
    // Exclude transient temp / failed messages: when AddMessage's `onCompleted` swaps an
    // optimistic `temp-…` for the real server message, the temp legitimately disappears from
    // the latest page and would otherwise be misclassified as a rolled-off "older" message,
    // ending up at the top of the conversation as a ghost.
    const newLatestPage = messageListData.conversation;
    const newIds = new Set(newLatestPage.map((m) => m.messageId));
    const rolledOff = prevLatestPageRef.current.filter((m) => {
      if (newIds.has(m.messageId)) return false;
      if (m.messageId?.startsWith('temp-')) return false;
      if (m.messageId?.includes(MESSAGE_STATUS.FAILED)) return false;
      return true;
    });
    if (rolledOff.length > 0) {
      setOlderMessages((existing) => {
        const existingIds = new Set(existing.map((m) => m.messageId));
        const additions = rolledOff.filter((m) => !existingIds.has(m.messageId));
        return additions.length > 0 ? [...existing, ...additions] : existing;
      });
    }
    prevLatestPageRef.current = newLatestPage;

    // Did conversation change
    if (currentConversationId.current !== conversationId) {
      // Scroll to bottom — do that here instead of in a useEffect so we only scroll after new data is finished loading
      setPrevMessageCount(messageListData.pagination.pageConversationCount);
      currentConversationId.current = conversationId;
      // Mark the next data refresh as a continuation of this conv switch so the
      // else-branch below treats it like the initial entry (force scroll-to-bottom)
      // even though `conversationId` no longer differs from `currentConversationId.current`.
      isAwaitingFreshDataAfterConvSwitchRef.current = true;
      scheduleScrollToBottom(500);
    } else {
      const currentFullMessageCount = messageListData.pagination.queryFullMessageCount;
      const { hasUnreadMessage } = messageListData;
      const isFirstDataAfterConvSwitch = isAwaitingFreshDataAfterConvSwitchRef.current;
      if (isFirstDataAfterConvSwitch) isAwaitingFreshDataAfterConvSwitchRef.current = false;

      if (hasUnreadMessage) {
        // Scroll the new message into view only when the user is already at the bottom; otherwise
        // surface the banner so we don't yank someone reading back through history. `isMessageListEndVisible`
        // (IntersectionObserver) is the source of truth here: it accounts for clipping by the scroll
        // container, so it correctly reports "not at the bottom" after the user scrolls up — including
        // after "Load more messages" prepends older pages, where the geometric viewport check used to
        // mis-report the end sentinel as visible.
        //
        // Treat the first data refresh after a conversation switch as if the user is
        // already at the bottom — the stale-cache pass already started the smooth
        // scroll, the visibility check would just race against it and incorrectly
        // surface the "new messages" banner instead of scrolling and marking read.
        const shouldScrollToMessagesEnd =
          (isMessageListEndVisible || isFirstDataAfterConvSwitch) &&
          !showPaginationLoading &&
          !preventMessageListScroll;

        // Only surface the banner when the list is actually visible. While the view is hidden
        // (mobile, viewing another panel) `isMessageListEndVisible` is false simply because the
        // column is `display: none`, not because the user scrolled up — showing the banner here
        // makes it flash when the conversation is re-opened.
        const shouldShowNewMessageNotification =
          isMessageListVisible && !shouldScrollToMessagesEnd && !showNewMessageNotification && !showPaginationLoading;

        if (shouldScrollToMessagesEnd) {
          scheduleScrollToBottom(500);
        } else if (shouldShowNewMessageNotification) {
          setShowNewMessageNotification(true);
        }
      } else if (!preventMessageListScroll && !showPaginationLoading) {
        // Scroll to bottom when hasUnreadMessage is false (i.e. sending a new message & polling for new messages)
        scheduleScrollToBottom(500);
      }

      // Invalidate getAttachmentsMetadata when new messages contain attachments
      // (only when shouldRefetchAttachments is true, e.g. inbox page)
      if (shouldRefetchAttachments && currentFullMessageCount && prevFullMessageCount !== currentFullMessageCount) {
        setPrevFullMessageCount(currentFullMessageCount);
        const { conversation: messages } = messageListData;
        const newMessageStartIndex = Math.max(0, messages.length - 1);
        const newMessageEndIndex = Math.max(
          0,
          newMessageStartIndex - (currentFullMessageCount - prevFullMessageCount) + 1,
        );
        for (let i = newMessageStartIndex; i >= newMessageEndIndex; i--) {
          const { attachments } = messages[i];
          if (attachments.length) {
            queryClient.invalidateQueries({
              queryKey: useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata.getKey({
                conversationId,
              }),
            });
            break;
          }
        }
      } else if (currentFullMessageCount && prevFullMessageCount !== currentFullMessageCount) {
        setPrevFullMessageCount(currentFullMessageCount);
      }
    }

    // Reset pagination loading messaging
    if (showPaginationLoading) setShowPaginationLoading(false);
    // Reset preventMessageListScroll when it was set by a consumer (e.g. DeleteAttachments modal)
    if (preventMessageListScroll) {
      preventMessageListScrollStore.set(false);
    }
    // Update error state to false to allow other components to render
    setHasMessageListError(false);
    setHasAuthError(false);
    // We intentionally only re-run this when the underlying data identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageListData, conversationId]);

  // Clear the conversation-switch scroll grace once the open scroll lands (end sentinel visible).
  // Opening an already-read conversation arms the grace but produces no fresh-data pass to consume
  // it, so it would otherwise stay set and make a later inbound message scroll-to-bottom instead of
  // bannering while the user is scrolled up. Doesn't affect RCRM-6650's unread-on-open scroll, whose
  // consuming pass runs while the scroll is still in flight (sentinel not yet visible).
  useEffect(() => {
    if (isMessageListEndVisible) {
      isAwaitingFreshDataAfterConvSwitchRef.current = false;
    }
  }, [isMessageListEndVisible]);

  // Equivalent of the previous Apollo `onError` callback
  useEffect(() => {
    if (!error) return;
    if (isHttpAuthError(error)) {
      bootstrap.monitor.count({ name: 'User not authorized to view conversation' });
      logger.info('User not authorized to view conversation - viewing authorization error message', {
        conversationId,
        listingAlias,
      });

      setHasMessageListError(true);
      setHasAuthError(true);
    } else {
      setHasMessageListError(true);
      setHasAuthError(false);
      logError({
        error,
        context: {
          api: 'getMessages',
          details: 'MessageList - Error fetching messages',
          conversationId: conversationId || '',
          listingAlias: listingAlias || '',
          clientTimeZone,
          isValidListingAlias: isValidListingAlias(listingAlias || ''),
          isValidConversationId: isValidConversationId(conversationId || ''),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (showPaginationLoading) setShowPaginationLoading(false);
    if (showNewMessageNotification) setShowNewMessageNotification(false);
    hasReceivedDataRef.current = false;
    prevLatestPageRef.current = [];
    // Drop accumulated older pages when switching conversations — they belong to the previous conversation.
    setOlderMessages([]);
    onConversationChange?.();
    // Only run when conversationId changes; onConversationChange is intentionally excluded to avoid extra runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Refetch this conversation's messages when a WebSocket event targets it. Owns its own
  // subscription rather than relying on a list hook to signal through a reactive variable.
  useMessageListRealtimeUpdates({ conversationId, refetchMessages: refetch });

  // The "new message" banner only appears when a message arrives while the user is scrolled up
  // (the data-arrival effect chooses the banner instead of an auto-scroll). The bug (RCRM-6650)
  // was that manually scrolling the end-of-list sentinel back into view — mouse wheel, trackpad,
  // scrollbar drag — didn't clear the banner or mark the conversation read, because that only
  // happened inside the programmatic `scrollToBottom`. Observe the sentinel and, when it becomes
  // visible *while the banner is showing*, clear the banner and mark read.
  //
  // Gating on `showNewMessageNotification` is essential, not just an optimization:
  //   1. It scopes the effect to the actual bug scenario (banner up). On conversation switch /
  //      page load there is no banner, so we don't fire here — the data-arrival effect already
  //      scrolls-to-bottom and marks read for that case.
  //   2. `onScrollComplete` → `markConversationAsRead` writes `hasUnreadMessage: false` into the
  //      messages query cache (always a new object reference). That re-runs the data-arrival
  //      effect, whose `else if (hasUnreadMessage === false)` branch schedules a
  //      `setTimeout(scrollToBottom, 500)`. Firing on every sentinel-visible event (e.g. right
  //      after the landing auto-scroll) would queue that scroll and yank a user who has since
  //      scrolled up — the regression this guard prevents.
  //   3. A banner can only exist after data has loaded, so this also avoids feeding a null
  //      `latestServerMessageId` into `RenterMessagingModal.handleScrollComplete`.
  // After clearing the banner the effect re-runs (dep changed) and the guard returns early, so
  // `onScrollComplete` fires exactly once per banner.
  useEffect(() => {
    if (!isMessageListEndVisible) return;
    if (!showNewMessageNotification) return;
    setShowNewMessageNotification(false);
    onScrollCompleteRef.current();
  }, [isMessageListEndVisible, showNewMessageNotification]);

  const handleOpenDeleteAttachmentsModal: HandleOpenDeleteAttachmentsModal = useCallback(
    ({ attachments, messageId }) => {
      setDeleteAttachmentsModalData({
        attachments,
        messageId,
      });
      setActiveModal(ModalType.DeleteAttachmentsModal);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
    [],
  );

  // The cache patch in DeleteAttachments covers the latest page; older "Load more"
  // pages live in `olderMessages` state and have to be patched here.
  const handleAttachmentsDeleted = useCallback((messageId: string, zdocIds: Array<string>) => {
    setOlderMessages((existing) => markMessageAttachmentsDeleted(existing, messageId, zdocIds));
  }, []);

  const handleOpenImagesViewer: HandleOpenImagesViewer = useCallback(({ imageAttachments, selectedImageIndex }) => {
    setImagesViewerData({
      imageAttachments,
      selectedImageIndex,
    });
    setActiveModal(ModalType.ImagesViewer);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  const handleSystemMessageCtaClick: OnSystemMessageCtaClick = useCallback(
    ({ messageType, url }) => {
      onSystemMessageCtaClick({ messageType, url });
    },
    [onSystemMessageCtaClick],
  );

  if (!messageListData && error) {
    return <ErrorContainer hasAuthError={hasAuthError} />;
  }

  if (showEmptyContainer) {
    return <EmptyContainer />;
  }

  // "Load more messages" — paginate by fetching the next older page and prepending it to
  // `olderMessages` (component state). Storing older pages here instead of in the React Query
  // cache prevents the polling fetch from overwriting them on the next 60s tick — the polled
  // query only ever holds the latest page.
  const handleLoadMoreMessages = async () => {
    // Find the oldest currently-displayed messageId across both older pages and the latest page.
    const oldestDisplayedMessageId = olderMessages[0]?.messageId ?? messageListData?.conversation?.[0]?.messageId;
    if (!oldestDisplayedMessageId) return;

    setShowPaginationLoading(true);
    const paginationPostData: SatelliteWebLandlordConversationGetMessagesRequestDto = {
      conversationId: conversationId || '',
      clientTimeZone,
      beforeMessageId: oldestDisplayedMessageId,
    };
    try {
      const rawResponse = await SatelliteWebLandlordConversationHandler_getMessages(paginationPostData);
      const olderPage = mapGetMessagesResponseToConversationData(rawResponse);

      setOlderMessages((existing) => {
        const existingIds = new Set(existing.map((m) => m.messageId));
        return [...olderPage.conversation.filter((m) => !existingIds.has(m.messageId)), ...existing];
      });
      setPrevMessageCount(olderPage.pagination.pageConversationCount);
    } catch (paginationError) {
      logError({
        error: paginationError,
        context: {
          api: 'getMessages',
          details: 'MessageListItems - Error loading older messages',
          conversationId: conversationId || '',
          listingAlias: listingAlias || '',
        },
      });
    } finally {
      setShowPaginationLoading(false);
    }
  };

  const handleCloseNewMessageNotification = () => {
    setShowNewMessageNotification(false);
  };

  let prevMessageDateMs: string;
  const latestPageMessages = messageListData?.conversation || [];
  // Combine the polling-managed latest page with the component-state-managed older pages.
  // Polling keeps `latestPageMessages` fresh; pagination accumulates into `olderMessages`.
  const conversationMessages: Array<ConversationMessage> = [...olderMessages, ...latestPageMessages];
  const pagination = messageListData?.pagination;
  // Surface the count of the *most recently loaded* page so the "Load more messages" button
  // appears whenever the most recent load returned a full page (i.e. there may be more).
  const currentPageConversationCount = prevMessageCount ?? (pagination ? pagination.pageConversationCount : null);
  const loading = isLoading;

  return (
    <>
      {activeModal === ModalType.ImagesViewer && (
        <ImagesViewer
          imageAttachments={imagesViewerData?.imageAttachments ?? []}
          selectedImageIndex={imagesViewerData?.selectedImageIndex || 0}
          listingAlias={conversation.listingAlias ?? ''}
          leadId={leadId}
          triggerLocation={triggerLocation}
        />
      )}

      {activeModal === ModalType.DeleteAttachmentsModal && (
        <DeleteAttachments
          attachments={deleteAttachmentsModalData?.attachments || []}
          messageId={deleteAttachmentsModalData?.messageId || ''}
          conversationId={conversationId}
          clientTimeZone={clientTimeZone}
          onAttachmentsDeleted={handleAttachmentsDeleted}
        />
      )}

      {showNewMessageNotification && (
        <NewMessageNotificationBanner onActionClick={scrollToBottom} onClose={handleCloseNewMessageNotification} />
      )}

      {showMoveToInboxBanner && (
        <Banner tone="info">To reply to this renter, tap Move to inbox to move the conversation to your inbox</Banner>
      )}

      <Box
        ref={ref}
        css={{
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          paddingLeft: 'default',
          paddingRight: 'tight',
          flex: 1,
          width: '100%',
          overscrollBehavior: 'contain',
          ...scrollbarStyles,
        }}
      >
        {!messageListData && loading ? (
          <MessageListContentsSkeleton />
        ) : (
          <>
            {showPaginationLoading && (
              <TextButton disabled css={{ margin: 'default' }}>
                Loading more messages...
              </TextButton>
            )}

            {!showPaginationLoading && currentPageConversationCount !== null && currentPageConversationCount >= 10 && (
              <TextButton onClick={handleLoadMoreMessages} css={{ margin: 'default' }}>
                Load more messages
              </TextButton>
            )}

            {!showPaginationLoading && currentPageConversationCount !== null && currentPageConversationCount < 10 && (
              <Box
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  alignItems: 'center',
                  marginTop: 'default',
                  marginBottom: 'tighter',
                  textAlign: 'center',
                }}
              >
                <Paragraph textStyle="body-sm-bold" css={{ color: 'text.subtle', marginBottom: 'tighter' }}>
                  {StringConstants.START_CONVERSATION_TEXT}
                </Paragraph>
                {conversationThreadWarningData && (
                  // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 570px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
                  <Paragraph textStyle="body-xs" css={{ textAlign: 'center', maxWidth: '570px' }}>
                    {conversationThreadWarningData.disclaimerText}{' '}
                    <Anchor href={conversationThreadWarningData.linkUrl} target="_blank" rel="noopener noreferrer">
                      {conversationThreadWarningData.linkText}
                    </Anchor>{' '}
                    {conversationThreadWarningData.linkSuffix}
                  </Paragraph>
                )}
              </Box>
            )}

            <List.Root appearance="none" css={{ marginBottom: 'loose' }}>
              {conversationMessages.map((conversationMessage, idx) => {
                let message = conversationMessage;
                let timestamp;
                const isLastMessageInList = idx === conversationMessages.length - 1;
                const { attachments, message: messageText, messageDateMs, messageId } = message;
                const isMessageSameDayAsPrevious = isSameDay(+messageDateMs, +prevMessageDateMs);
                const key = `${messageId}-${messageDateMs}`;
                const isDisplayableDate = isDisplayableTimestamp(messageDateMs);

                // An unusable timestamp must not become the previous day, or the next real message
                // would compare against epoch 0 and repeat a separator it already rendered.
                if (isDisplayableDate) {
                  prevMessageDateMs = messageDateMs;
                }

                // The shared message components format this with date-fns as well
                // (`getMessageTimestampText`) — blank an unusable value here so no downstream
                // formatter can be handed one.
                if (!isDisplayableDate) {
                  message = { ...message, messageDateMs: '' };
                }

                // date-fns throws RangeError on an invalid date, which would take the whole page
                // down from inside this render — drop the day separator instead.
                if (isDisplayableDate && !isMessageSameDayAsPrevious) {
                  if (isYesterday(+messageDateMs)) {
                    timestamp = 'Yesterday';
                  } else if (isToday(+messageDateMs)) {
                    timestamp = 'Today';
                  } else {
                    const fnsFormat = isThisYear(new Date(+messageDateMs))
                      ? DATE_FNS_FORMAT
                      : DATE_FNS_FORMAT_WITH_YEAR;
                    timestamp = format(new Date(+messageDateMs), fnsFormat);
                  }
                }

                // Edge case: message objects with missing message AND attachments fields - return "failed to load" item
                if (!messageText && !attachments) {
                  message = {
                    ...message,
                    message: MESSAGE_LOAD_ERROR_TEXT,
                    messageType: FAILED_TO_LOAD_MESSAGE_TYPE,
                    uiTreatmentType: SatelliteUiTreatmentType.LOW_PRIORITY,
                  };

                  return (
                    <MessageItem
                      conversation={conversation}
                      leadId={leadId}
                      isLastMessageInList={isLastMessageInList}
                      key={key}
                      message={message}
                      onResendMessageComplete={onResendMessageComplete}
                      resendBlockedText={resendBlockedText}
                      onSystemMessageCtaClick={handleSystemMessageCtaClick}
                      openDeleteAttachmentsModal={handleOpenDeleteAttachmentsModal}
                      openImagesViewer={handleOpenImagesViewer}
                      triggerLocation={triggerLocation}
                    />
                  );
                }

                // Edge case: message field with no text AND no attachments - do not render
                if (stringUtils.isEmptyString(messageText || '') && attachments.length === 0) {
                  return null;
                }

                return (
                  <React.Fragment key={key}>
                    {timestamp && (
                      <Text
                        asChild
                        textStyle="body-xs"
                        css={{
                          display: 'block',
                          color: 'text.subtle',
                          marginBlock: 'default',
                          textAlign: 'center',
                        }}
                      >
                        <time>{timestamp}</time>
                      </Text>
                    )}
                    {idx === 0 && (currentPageConversationCount || 0) < 10 && (
                      <RenterProfileChatBubble
                        conversationId={conversationId}
                        clientTimeZone={clientTimeZone}
                        onRenterProfileClick={onRenterProfileClick}
                        shouldShowViewRenterProfileButton={triggerLocation === TRIGGER_LOCATION.LeadManagementPage}
                      />
                    )}
                    <MessageItem
                      conversation={conversation}
                      leadId={leadId}
                      isLastMessageInList={isLastMessageInList}
                      message={message}
                      onResendMessageComplete={onResendMessageComplete}
                      resendBlockedText={resendBlockedText}
                      onSystemMessageCtaClick={handleSystemMessageCtaClick}
                      openDeleteAttachmentsModal={handleOpenDeleteAttachmentsModal}
                      openImagesViewer={handleOpenImagesViewer}
                      triggerLocation={triggerLocation}
                    />
                  </React.Fragment>
                );
              })}
            </List.Root>
          </>
        )}
        <Box ref={messageListEndRef} css={{ paddingBottom: 'loose' }} />
      </Box>
    </>
  );
});

export default MessageListItems;
